import { Button, Descriptions, Modal, Tag, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '@shared/utils/dateFormatter';
import { buildSupportText, commitsDiffer, formatVersionLabel, frontendCommit } from '@shared/utils/buildInfo';
import { copyToClipboard } from '@shared/lib/copyToClipboard';
import type { SystemInfo } from '@shared/types/system';

interface AboutModalProps {
  open: boolean;
  onClose: () => void;
  info?: SystemInfo;
}

/** Окно «О системе»: сведения о сборке и копирование одной строки для поддержки. */
export function AboutModal({ open, onClose, info }: AboutModalProps) {
  const { t } = useTranslation(['translation']);
  const [messageApi, contextHolder] = message.useMessage();

  const handleCopy = async () => {
    if (!info) return;
    const result = await copyToClipboard(buildSupportText(info, frontendCommit()));
    if (result === 'copied') messageApi.success(t('about.copied'));
    else messageApi.error(t('about.copyFailed'));
  };

  const uiCommit = frontendCommit();
  const mismatch = commitsDiffer(info?.commit, uiCommit);
  const builtAt = info?.builtAt ? new Date(info.builtAt) : undefined;
  const builtAtText =
    builtAt && !Number.isNaN(builtAt.getTime())
      ? builtAt.toLocaleString(currentLocale())
      : undefined;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t('about.title')}
      width={480}
      footer={
        <Button icon={<CopyOutlined />} onClick={handleCopy} disabled={!info}>
          {t('about.copyForSupport')}
        </Button>
      }
    >
      {contextHolder}
      {info ? (
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label={t('about.product')}>{info.companyName}</Descriptions.Item>
          <Descriptions.Item label={t('about.version')}>
            <span className="font-mono">{formatVersionLabel(info.version) || '—'}</span>
            {info.dirty && (
              <Tag color="warning" style={{ marginLeft: 8 }}>
                {t('about.dirty')}
              </Tag>
            )}
          </Descriptions.Item>
          {info.commit && (
            <Descriptions.Item label={t('about.commit')}>
              <span className="font-mono">{info.commit.slice(0, 8)}</span>
            </Descriptions.Item>
          )}
          {builtAtText && (
            <Descriptions.Item label={t('about.commitTime')}>{builtAtText}</Descriptions.Item>
          )}
          {uiCommit && (
            <Descriptions.Item label={t('about.frontend')}>
              <span className="font-mono">{uiCommit.slice(0, 8)}</span>
              {mismatch && (
                <Tag color="warning" style={{ marginLeft: 8 }}>
                  {t('about.mismatch')}
                </Tag>
              )}
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t('about.domain')}>{info.domain}</Descriptions.Item>
        </Descriptions>
      ) : (
        t('about.unavailable')
      )}
    </Modal>
  );
}
